import { environmentConfig } from "@src/appV2/environment";
import { AdminMenuPriviliges } from "../priviligesProviders/AdminMenuPriviligesProvider";
import { NavigationItem, NavigationMenu, NavigationSection } from "./types";
import { getRouteAndSelected } from "./utils";

export function getAdminNavigationMenu(
  adminMenuPriviliges: AdminMenuPriviliges,
  currentPath: string
): NavigationMenu {
  const items: Array<NavigationItem | NavigationSection> = [];

  if (adminMenuPriviliges.accountsSection) {
    const sectionItems: NavigationItem[] = [];

    if (adminMenuPriviliges.accountsSection.hcps) {
      sectionItems.push({
        label: "HCPs",
        iconType: "person-male-formal",
        ...getRouteAndSelected("adminAccountsAgents", currentPath),
      });
    }
    if (adminMenuPriviliges.accountsSection.workplaces) {
      sectionItems.push({
        label: "Workplaces",
        iconType: "building-thick",
        ...getRouteAndSelected("adminAccountsFacilities", currentPath),
      });
    }
    if (adminMenuPriviliges.accountsSection.employees) {
      sectionItems.push({
        label: "Employees",
        iconType: "people",
        ...getRouteAndSelected("adminAccountsEmployees", currentPath),
      });
    }
    if (adminMenuPriviliges.accountsSection.requirements) {
      sectionItems.push({
        label: "Requirements",
        iconType: "notepad-alt",
        ...getRouteAndSelected("adminAccountsRequirement", currentPath),
      });
    }
    if (adminMenuPriviliges.accountsSection.locations) {
      sectionItems.push({
        label: "Locations",
        iconType: "location-alt",
        ...getRouteAndSelected("adminAccountsLocations", currentPath),
      });
    }
    if (adminMenuPriviliges.accountsSection.changeLog) {
      sectionItems.push({
        label: "Change Log",
        iconType: "stack-vertical",
        ...getRouteAndSelected("adminAccountsChangeLog", currentPath),
      });
    }
    if (adminMenuPriviliges.accountsSection.dnrs) {
      sectionItems.push({
        label: "DNRs",
        iconType: "padlock-locked",
        ...getRouteAndSelected("adminAccountsExclusions", currentPath),
      });
    }

    items.push({ label: "Accounts", items: sectionItems });
  }

  if (adminMenuPriviliges.shiftsSection) {
    const sectionItems: NavigationItem[] = [];

    if (adminMenuPriviliges.shiftsSection.shiftSearch) {
      sectionItems.push({
        label: "Shift Search",
        iconType: "magnifying-glass",
        ...getRouteAndSelected("adminShifts", currentPath),
      });
    }
    if (adminMenuPriviliges.shiftsSection.shiftsVerification) {
      sectionItems.push({
        label: "Shifts Verification",
        iconType: "badge-check",
        isSelected: false,
        onClick: () => {
          window.open(
            environmentConfig.REACT_APP_SHIFT_VERIFICATION_BASE_URL,
            "_blank",
            "noopener,noreferrer"
          );
        },
      });
    }
    if (adminMenuPriviliges.shiftsSection.shiftLogs) {
      sectionItems.push({
        label: "Shift Logs",
        iconType: "folder-view",
        ...getRouteAndSelected("adminLogsShift", currentPath),
      });
    }
    if (adminMenuPriviliges.shiftsSection.timeclockRequests) {
      sectionItems.push({
        label: "Timeclock Requests",
        iconType: "clock",
        ...getRouteAndSelected("adminTimeclock", currentPath),
      });
    }
    if (adminMenuPriviliges.shiftsSection.noShows) {
      sectionItems.push({
        label: "No Shows",
        iconType: "gps-disabled",
        ...getRouteAndSelected("adminUrgentShiftNcns", currentPath),
      });
    }

    items.push({ label: "Shifts", items: sectionItems });
  }

  if (adminMenuPriviliges.invoiceSection) {
    const sectionItems: NavigationItem[] = [];

    if (adminMenuPriviliges.invoiceSection.byPeriod) {
      sectionItems.push({
        label: "By Period",
        iconType: "centered-circle",
        ...getRouteAndSelected("adminAccountsInvocingByPeriod", currentPath),
      });
    }
    if (adminMenuPriviliges.invoiceSection.byNumber) {
      sectionItems.push({
        label: "By Number",
        iconType: "dash",
        ...getRouteAndSelected("adminAccountsInvocingByNumber", currentPath),
      });
    }
    if (adminMenuPriviliges.invoiceSection.postInvoiceDisputes) {
      sectionItems.push({
        label: "Post-Invoice Disputes",
        iconType: "caret-double-right",
        ...getRouteAndSelected("adminAccountsPostInvoiceDisputes", currentPath),
      });
    }
    if (adminMenuPriviliges.invoiceSection.preInvoiceDisputes) {
      sectionItems.push({
        label: "Pre-Invoice Disputes",
        iconType: "caret-double-left",
        ...getRouteAndSelected("adminAccountsPreInvoiceDisputes", currentPath),
      });
    }

    items.push({ label: "Invoice", items: sectionItems });
  }

  if (adminMenuPriviliges.payrollSection) {
    const sectionItems: NavigationItem[] = [];

    if (adminMenuPriviliges.payrollSection.proofOfEarning) {
      sectionItems.push({
        label: "Payroll",
        iconType: "money-dollar",
        ...getRouteAndSelected("agentProof", currentPath),
      });
    }
    if (adminMenuPriviliges.payrollSection.shiftPayments) {
      sectionItems.push({
        label: "Shift Payments",
        iconType: "palm-dollar",
        ...getRouteAndSelected("adminShiftPaymentsPayRates", currentPath),
      });
    }

    items.push({ label: "Payroll", items: sectionItems });
  }

  if (adminMenuPriviliges.adminToolsSection) {
    const sectionItems: NavigationItem[] = [];

    if (adminMenuPriviliges.adminToolsSection.timekeepingVideos) {
      sectionItems.push({
        label: "Timekeeping Videos",
        iconType: "replay",
        ...getRouteAndSelected("adminToolsTimekeepingVideos", currentPath),
      });
    }
    if (adminMenuPriviliges.adminToolsSection.shiftExplainer) {
      sectionItems.push({
        label: "Shift Explainer",
        iconType: "information-circle",
        ...getRouteAndSelected("adminToolsShiftExplainer", currentPath),
      });
    }
    if (adminMenuPriviliges.adminToolsSection.jobsScraper) {
      sectionItems.push({
        label: "Jobs Scraper",
        iconType: "briefcase",
        ...getRouteAndSelected("adminToolsJobsScraper", currentPath),
      });
    }
    if (adminMenuPriviliges.adminToolsSection.badgeVerificationGrading) {
      sectionItems.push({
        label: "Badge Verification Grading",
        iconType: "badge-check",
        ...getRouteAndSelected("adminToolsBadgeVerificationGrading", currentPath),
      });
    }
    if (adminMenuPriviliges.adminToolsSection.ssnWorkerLookup) {
      sectionItems.push({
        label: "SSN Worker Lookup",
        iconType: "magnifying-glass",
        ...getRouteAndSelected("adminToolsSsnWorkerLookup", currentPath),
      });
    }

    items.push({ label: "Admin Tools", items: sectionItems });
  }

  if (adminMenuPriviliges.staffingSection) {
    const sectionItems: NavigationItem[] = [];

    if (adminMenuPriviliges.staffingSection.qualifications) {
      sectionItems.push({
        label: "Qualifications",
        iconType: "medal-star-alt",
        ...getRouteAndSelected("adminManageQualifications", currentPath),
      });
    }
    if (adminMenuPriviliges.staffingSection.workplaceTypes) {
      sectionItems.push({
        label: "Workplace Types",
        iconType: "house-building",
        ...getRouteAndSelected("adminManageWorkplaceTypes", currentPath),
      });
    }
    if (adminMenuPriviliges.staffingSection.skillAssessments) {
      sectionItems.push({
        label: "Skill Assessments",
        iconType: "files",
        ...getRouteAndSelected("adminManageSkillsAssessments", currentPath),
      });
    }

    items.push({ label: "Staffing", items: sectionItems });
  }

  if (adminMenuPriviliges.hiringSection) {
    const sectionItems: NavigationItem[] = [];

    if (adminMenuPriviliges.hiringSection.availability) {
      sectionItems.push({
        label: "Availability",
        iconType: "clock",
        ...getRouteAndSelected("adminPlacementsAvailability", currentPath),
      });
    }

    if (adminMenuPriviliges.hiringSection.bans) {
      sectionItems.push({
        label: "Bans",
        iconType: "notepad-problem",
        ...getRouteAndSelected("adminAccountsPlacementCandidateBans", currentPath),
      });
    }

    if (adminMenuPriviliges.hiringSection.interviews) {
      sectionItems.push({
        label: "Interviews",
        iconType: "calendar",
        ...getRouteAndSelected("adminAccountsInterviews", currentPath),
      });
    }

    items.push({ label: "Hiring", items: sectionItems });
  }

  return { items };
}
